import { useState, useEffect } from 'react'
import { Eye, EyeOff, Shield, CheckCircle, X } from 'lucide-react'

interface PasswordData {
  password: string
  confirmPassword: string
}

interface PasswordStepProps {
  data?: PasswordData
  onChange: (data: Partial<PasswordData>) => void
  onValidationChange: (isValid: boolean) => void
  errors?: Record<string, string>
}

// Requisitos de senha
const PASSWORD_REQUIREMENTS = {
  minLength: 8,
  hasUppercase: true,
  hasLowercase: true,
  hasNumber: true,
  hasSpecialChar: true
}

export function PasswordStep({ data, onChange, onValidationChange }: PasswordStepProps) {
  const [password, setPassword] = useState(data?.password || '')
  const [confirmPassword, setConfirmPassword] = useState(data?.confirmPassword || '')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [passwordStrength, setPasswordStrength] = useState(0)
  const [, setPasswordErrors] = useState<string[]>([])

  // Verificar força da senha
  const checkPasswordStrength = (pwd: string) => {
    let strength = 0
    const errors: string[] = []

    // Comprimento mínimo
    if (pwd.length >= PASSWORD_REQUIREMENTS.minLength) {
      strength += 20
    } else {
      errors.push('Mínimo 8 caracteres')
    }

    // Letra maiúscula
    if (/[A-Z]/.test(pwd)) {
      strength += 20
    } else {
      errors.push('Uma letra maiúscula')
    }

    // Letra minúscula
    if (/[a-z]/.test(pwd)) {
      strength += 20
    } else {
      errors.push('Uma letra minúscula')
    }

    // Número
    if (/\d/.test(pwd)) {
      strength += 20
    } else {
      errors.push('Um número')
    }

    // Caractere especial
    if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd)) {
      strength += 20
    } else {
      errors.push('Um caractere especial (!@#$%)')
    }

    setPasswordStrength(strength)
    setPasswordErrors(errors)
    return { strength, errors }
  }

  // Validação de senha
  const validatePassword = (pwd: string, confirm: string) => {
    const { strength } = checkPasswordStrength(pwd)

    // Confirmação deve estar preenchida e coincidir
    if (!confirm || pwd !== confirm) {
      return false
    }

    // Senha forte o suficiente
    return strength === 100
  }

  // Atualizar dados e validação
  useEffect(() => {
    const isValid = validatePassword(password, confirmPassword)
    onValidationChange(isValid)

    onChange({
      password,
      confirmPassword
    })
  }, [password, confirmPassword])

  const handlePasswordChange = (value: string) => {
    setPassword(value)
    if (confirmPassword && value !== confirmPassword) {
      // Se já tem confirmação, verificar se ainda coincidem
    }
  }

  const getStrengthColor = (strength: number) => {
    if (strength <= 20) return 'bg-red-500'
    if (strength <= 40) return 'bg-orange-500'
    if (strength <= 60) return 'bg-yellow-500'
    if (strength <= 80) return 'bg-blue-500'
    return 'bg-green-500'
  }

  const getStrengthText = (strength: number) => {
    if (strength <= 20) return 'Muito fraca'
    if (strength <= 40) return 'Fraca'
    if (strength <= 60) return 'Média'
    if (strength <= 80) return 'Forte'
    return 'Muito forte'
  }

  return (
    <div className="space-y-6">

      <div className="max-w-md mx-auto space-y-4">
        {/* Campo Senha */}
        <div>
          <label className="block text-sm font-medium text-brand-text mb-2">
            Senha *
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => handlePasswordChange(e.target.value)}
              placeholder="Digite sua senha"
              className="w-full px-4 py-3 pr-12 border border-brand-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary bg-white dark:bg-brand-surface text-brand-text"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-brand-text-muted hover:text-brand-text"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          {/* Indicador de força */}
          {password && (
            <div className="mt-2">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-brand-text-muted">
                  Força da senha
                </span>
                <span className="text-xs text-brand-text-muted">
                  {getStrengthText(passwordStrength)}
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-brand-border rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-300 ${getStrengthColor(passwordStrength)}`}
                  style={{ width: `${passwordStrength}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Campo Confirmar Senha */}
        <div>
          <label className="block text-sm font-medium text-brand-text mb-2">
            Confirmar senha *
          </label>
          <div className="relative">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Digite sua senha novamente"
              className="w-full px-4 py-3 pr-12 border border-brand-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary bg-white dark:bg-brand-surface text-brand-text"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-brand-text-muted hover:text-brand-text"
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          {/* Erro de confirmação */}
          {password && confirmPassword && password !== confirmPassword && (
            <div className="mt-1 text-red-600 dark:text-red-400 text-sm flex items-center gap-1">
              <X size={14} />
              As senhas não coincidem
            </div>
          )}
        </div>

        {/* Requisitos da senha */}
        <div className="bg-brand-surface-alt rounded-lg p-4">
          <h3 className="text-sm font-medium text-brand-text mb-3">
            Requisitos da senha:
          </h3>
          <div className="space-y-2">
            {[
              { text: 'Mínimo 8 caracteres', valid: password.length >= 8 },
              { text: 'Letra maiúscula', valid: /[A-Z]/.test(password) },
              { text: 'Letra minúscula', valid: /[a-z]/.test(password) },
              { text: 'Número', valid: /\d/.test(password) },
              { text: 'Caractere especial', valid: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password) }
            ].map((requirement, index) => (
              <div key={index} className="flex items-center gap-2">
                {requirement.valid ? (
                  <CheckCircle size={14} className="text-green-500" />
                ) : (
                  <X size={14} className="text-gray-400" />
                )}
                <span className={`text-sm ${requirement.valid ? 'text-green-600 dark:text-green-400' : 'text-brand-text-muted'}`}>
                  {requirement.text}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Dicas de segurança */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
            <div className="text-sm text-blue-800 dark:text-blue-200">
              <p className="font-medium mb-1">Dica de segurança:</p>
              <p className="text-blue-700 dark:text-blue-300">
                Use uma combinação única que você não usa em outros sites.
                Evite datas, nomes ou informações pessoais.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
